import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Chip } from "./EndpointCard";
import { FLAG, REC_LABEL } from "./syncText";

const PAGE = 10;

function Filters({ q, setQ, status, setStatus, sort, setSort }) {
  const sel = "h-8 rounded-md bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-xs text-slate-300 px-2";
  return (
    <div className="flex flex-wrap gap-2">
      <Input data-testid="recon-search" value={q} onChange={(e) => setQ(e.target.value.toUpperCase().slice(0, 40))} placeholder="Search symbol" className="h-8 max-w-[180px] bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-xs" />
      <select data-testid="recon-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
        <option value="">All statuses</option>
        {Object.entries(REC_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <select data-testid="recon-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={sel}>
        <option value="status">Sort: status</option>
        <option value="symbol">Sort: symbol</option>
      </select>
    </div>
  );
}

function Rows({ rows }) {
  if (!rows.length) return <div data-testid="recon-real-empty" className="py-4 text-center text-xs text-slate-500">No matching records.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="recon-real-table">
        <thead><tr className="text-[10px] text-slate-500 text-left"><th className="py-1 pr-3">Symbol</th><th className="pr-3">Status</th><th className="pr-3 text-right">Sharekhan qty</th><th className="pr-3 text-right">Tradify qty</th><th className="text-right">Diff</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.symbol}-${r.status}`} data-testid={`recon-real-row-${r.symbol}`} className="border-t border-[var(--border-subtle)]">
              <td className="py-1.5 pr-3 font-mono text-slate-200">{r.symbol}</td>
              <td className="pr-3"><Chip status={r.status === "matched" ? "matched" : r.unverified ? "unverified" : "partial"} testId={`recon-real-status-${r.symbol}`} /> <span className="text-slate-400 ml-1">{REC_LABEL[r.status] || r.status}</span></td>
              <td className="pr-3 text-right font-mono text-slate-400">{r.broker_qty ?? r.net_quantity ?? (r.broker_records ? `${r.broker_records} rows` : "—")}</td>
              <td className="pr-3 text-right font-mono text-slate-400">{r.app_qty ?? (r.tradify_records ? `${r.tradify_records} rows` : "—")}</td>
              <td className="text-right font-mono text-slate-400">{r.qty_difference ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Excluded({ ex }) {
  if (!ex) return null;
  return (
    <div data-testid="recon-demo-excluded" className="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-slate-400">
      <span className="text-violet-300 font-mono uppercase text-[10px] mr-2">Demo / paper — excluded</span>
      {ex.demo.count} demo holding(s){ex.demo.symbols.length ? `: ${ex.demo.symbols.join(", ")}` : ""}
      {ex.unlabelled.count > 0 && <> · {ex.unlabelled.count} unlabelled record(s) excluded: {ex.unlabelled.symbols.join(", ")}</>}
      <div className="text-slate-500">Never compared with real Sharekhan data and never changed by reconciliation.</div>
    </div>
  );
}

export function ReconciliationSection() {
  const [q, setQ] = useState(""); const [status, setStatus] = useState(""); const [sort, setSort] = useState("status"); const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["sharekhan-real-recon", q, status, sort, page],
    queryFn: async () => (await api.get("/brokers/sharekhan/reconciliation", { params: { q, status: status || undefined, sort, page, page_size: PAGE } })).data,
    placeholderData: keepPreviousData,
  });
  const reset = (fn) => (v) => { fn(v); setPage(1); };
  if (isLoading) return <div data-testid="recon-real-loading" className="text-xs text-slate-500">Loading reconciliation…</div>;
  if (isError) return <div data-testid="recon-real-error" className="text-xs text-rose-300">Reconciliation could not be loaded. <button className="underline" onClick={() => refetch()}>Retry</button></div>;
  const pages = Math.max(1, Math.ceil((data.filtered_total || 0) / PAGE));
  return (
    <div className="space-y-2" data-testid="recon-real-section">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-200">Real holdings reconciliation</span>
        <Chip status={data.status} testId="recon-real-overall" />
        <span className="text-[10px] text-slate-500">Advisory only · ledger changes: {data.ledger_changes} · real Tradify records: {data.tradify_real_records ?? 0}</span>
      </div>
      {data.flags.map((f) => <div key={f} data-testid={`recon-flag-${f}`} className="text-[11px] text-amber-300">• {FLAG[f] || f}</div>)}
      <Filters q={q} setQ={reset(setQ)} status={status} setStatus={reset(setStatus)} sort={sort} setSort={setSort} />
      <Rows rows={data.rows} />
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span data-testid="recon-real-count">{data.filtered_total} record(s)</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="recon-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-7 border-[var(--border-subtle)]">Prev</Button>
          <span data-testid="recon-page">{page} / {pages}</span>
          <Button size="sm" variant="outline" data-testid="recon-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="h-7 border-[var(--border-subtle)]">Next</Button>
        </div>
      </div>
      <Excluded ex={data.excluded} />
    </div>
  );
}
